<script setup lang="ts">
  import { computed, ref } from 'vue';
  import type { AccountUser } from '@/features/account';
  import { OfficeTourButton } from '@/features/officeTour';
  import { useStickyOnScroll } from '@/shared/composables';
  import { SiteHeader } from '@/widgets/siteHeader';

  interface SerpLayoutProps {
    user?: AccountUser | null;
    accountUrl?: string;
  }
  defineProps<SerpLayoutProps>();

  interface SerpLayoutEmits {
    (e: 'login'): void;
    (e: 'logout'): void;
    (e: 'buy'): void;
  }
  const emit = defineEmits<SerpLayoutEmits>();

  const headerEl = ref<HTMLElement | null>(null);
  const formEl = ref<HTMLElement | null>(null);
  const { collapsed } = useStickyOnScroll({ target: headerEl });

  const formHeight = computed<string | null>(() => (formEl.value ? `${formEl.value.offsetHeight}px` : null));
</script>

<template>
  <div
    :class="['serp-page', { 'serp-page_collapsed': collapsed }]"
    data-testid="serp-page"
  >
    <div
      ref="headerEl"
      class="serp-page__header"
      :style="{ opacity: collapsed ? 0 : 1 }"
    >
      <slot name="header">
        <SiteHeader
          :user="user"
          :accountUrl="accountUrl"
          @login="emit('login')"
          @logout="emit('logout')"
        >
          <template #action>
            <OfficeTourButton @buy="emit('buy')" />
          </template>
        </SiteHeader>
      </slot>

      <!-- Заголовок внутри шапки: уезжает вместе с ней при скролле, форма прилипает. -->
      <div
        v-if="$slots.title"
        class="serp-page__title"
      >
        <slot name="title" />
      </div>
    </div>

    <div
      v-if="collapsed && formHeight"
      :style="{ height: formHeight }"
    ></div>
    <div
      ref="formEl"
      :class="['serp-page__form', { 'serp-page__form_fixed': collapsed }]"
    >
      <slot
        name="form"
        :collapsed="collapsed"
      />
    </div>

    <slot name="under-form" />
  </div>
</template>

<style scoped lang="scss">
  .serp-page {
    position: relative;

    &__header {
      position: relative;
      z-index: 3;
    }

    &__title {
      @include header-background;

      box-sizing: border-box;
      display: flex;
      justify-content: center;
      padding: 0 var(--spacing-l) var(--spacing-xl);
      width: 100%;
    }

    &__form {
      top: 0;
      z-index: 2;

      &_fixed {
        position: fixed;
        width: 100%;
      }
    }
  }
</style>
